import type {StyleXArray} from '@stylexjs/stylex';
import {props} from '@stylexjs/stylex';
import {getIntents} from '../intent';
import {StyleSheets} from '../stylesheets';
import {VIEW_CLASS} from './constants';
import {StyleCache} from './StyleCache';
import {addClassName, addStyle} from './util';
import type {Style} from './types';
import type {View} from '../view';
import {getRegisteredCssSerializer} from '../view/css';
import type {StyleXStyles} from '../stylex/intent';
import type {CssPolicy} from '../providers/EngineConfigProvider';
import {
  decorateStyleIntent,
  lazyGlobalStyleIntent,
  lazyStyleIntent,
} from './intents';
import {lazyGlobalTokenIntent, lazyTokenIntent} from '../token/intents';
import {tokenCss} from '../token/css';

const scheduledIntents = [] as Style.Intent[];
let lastInsertedIndex = 0;

StyleSheets.onFlush(() => {
  lastInsertedIndex = 0;
});

export function scheduleStyleIntent(intent: Style.Intent): void {
  if (
    intent.layer !== 'global' &&
    !lazyGlobalStyleIntent.isIntent(intent) &&
    !lazyGlobalTokenIntent.isIntent(intent)
  ) {
    throw new Error('Only styles on the global layer can be scheduled.');
  }

  scheduledIntents.push(intent);
}

export function runStyleIntents<Props>(
  revision: View.ViewRevision<Props>,
): void {
  // Directly insert any scheduled global style intents.
  // We do this because these intents can be scheduled at any time, and
  // therefore are not deterministically associated with rendering the current
  // component. This is safe to do because they are fully global and don't
  // produce class names to be rendered on the current component's element.
  for (let i = lastInsertedIndex; i < scheduledIntents.length; i++) {
    // Directly insert global styles.
    StyleCache.insert([scheduledIntents[i].key]);
  }
  lastInsertedIndex = scheduledIntents.length;

  // Gather and insert any style intents for the current component.
  const intents = getIntents(decorateStyleIntent, revision);

  const className = intents
    ? StyleCache.insert(intents.map(({key}) => key))
    : VIEW_CLASS;

  addClassName(revision.props as object, className);
}

export function runLazyStyleIntents<Props, Options extends View.ConfigOptions>(
  revision: View.ViewRevision<Props>,
  options: Options,
  cssPolicy?: CssPolicy,
): void {
  const css = getRegisteredCssSerializer(options.css) as
    | Style.Create<Style.Properties>
    | undefined;

  // Directly insert any scheduled global style intents.
  // We do this because these intents can be scheduled at any time, and
  // therefore are not deterministically associated with rendering the current
  // component. This is safe to do because they are fully global and don't
  // produce class names to be rendered on the current component's element.
  for (let i = lastInsertedIndex; i < scheduledIntents.length; i++) {
    const maybeIntent = scheduledIntents[i];
    let intent = maybeIntent;

    if (css && lazyGlobalStyleIntent.isIntent(maybeIntent)) {
      intent = css.global(maybeIntent);
    } else if (lazyGlobalTokenIntent.isIntent(maybeIntent)) {
      intent = tokenCss.global(maybeIntent);
    }

    // Directly insert global styles.
    StyleCache.insert([intent.key]);
  }
  lastInsertedIndex = scheduledIntents.length;

  const styleIntents = getIntents(lazyStyleIntent, revision) || [];
  const globalStyleIntents = getIntents(lazyGlobalStyleIntent, revision) || [];
  const tokenIntents = getIntents(lazyTokenIntent, revision) || [];
  const globalTokenIntents = getIntents(lazyGlobalTokenIntent, revision) || [];
  const hasIntents =
    styleIntents.length > 0 ||
    globalStyleIntents.length > 0 ||
    tokenIntents.length > 0 ||
    globalTokenIntents.length > 0;

  if (css && hasIntents) {
    const classnames: string[] = [];
    const compiledStyles: StyleXStyles[] = [];

    for (let i = 0; i < styleIntents.length; i++) {
      if (styleIntents[i]) {
        const [raw, computed] = styleIntents[i];

        switch (cssPolicy) {
          // @ts-expect-error - it's expected that we fall through to the next case
          case 'static-only': {
            if (process.env.NODE_ENV === 'development') {
              if (!computed) {
                throw new Error(
                  `The '${
                    options.name || revision.Component
                  }' component is being rendered in 'static-only' mode but runtime styles are being provided. Please either update the 'cssPolicy' to 'static-first | runtime-only' or compile runtime styles: ${JSON.stringify(
                    raw,
                    null,
                    2,
                  )}.`,
                );
              }
            }
          }
          // eslint-disable-next-line no-fallthrough
          case 'static-first': {
            if (computed) {
              compiledStyles.push(computed);
            } else {
              classnames.push(css(raw).key);
            }
            break;
          }
          case 'runtime-only':
          default: {
            classnames.push(css(raw).key);
            break;
          }
        }
      }
    }

    for (let i = 0; i < globalStyleIntents.length; i++) {
      if (globalStyleIntents[i]) {
        classnames.push(css.global(globalStyleIntents[i]).key);
      }
    }

    for (let i = 0; i < tokenIntents.length; i++) {
      if (tokenIntents[i]) {
        classnames.push(tokenCss(tokenIntents[i]).key);
      }
    }

    for (let i = 0; i < globalTokenIntents.length; i++) {
      if (globalTokenIntents[i]) {
        classnames.push(tokenCss.global(globalTokenIntents[i]).key);
      }
    }

    if (compiledStyles.length > 0) {
      const {className, style} = props(
        ...(compiledStyles as StyleXArray<any>), // eslint-disable-line @typescript-eslint/no-explicit-any
      );

      if (className) {
        addClassName(revision.props as {className?: string}, className);
      }

      if (style) {
        addStyle(revision.props as {style?: React.CSSProperties}, style);
      }
    }

    addClassName(revision.props as object, StyleCache.insert(classnames));
  } else {
    addClassName(revision.props as object, VIEW_CLASS);
  }
}
